"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import {
  createSessionToken,
  getAuthSecret,
  isAuthEnabled,
  validatePassword,
  validatePin,
  isPinConfigured,
  SESSION_COOKIE_NAME,
  SESSION_MAX_AGE,
} from "../auth";
import {
  checkLoginAllowed,
  clientIpFromHeaders,
  lockedMessage,
  recordLoginFailure,
  recordLoginSuccess,
} from "../login-rate-limit";

/** IP do cliente (fora de um request, como nos testes, cai em "unknown"). */
async function currentClientIp(): Promise<string> {
  try {
    return clientIpFromHeaders(await headers());
  } catch {
    return "unknown";
  }
}

export async function verifyPinAction(pin: string): Promise<{ success: boolean; error?: string }> {
  // Se nem APP_PIN nem APP_PASSWORD estiverem configurados
  if (!isPinConfigured() && !isAuthEnabled()) {
    return {
      success: false,
      error: "Nenhum PIN ou senha configurado no arquivo .env.",
    };
  }

  // O PIN aceita a senha mestra (recuperação), então divide o limite de tentativas com o login.
  const ip = await currentClientIp();
  const gate = checkLoginAllowed(ip);
  if (!gate.allowed) {
    return { success: false, error: lockedMessage(gate.retryAfterSec) };
  }

  const isValid = validatePin(typeof pin === "string" ? pin : "");
  if (!isValid) {
    recordLoginFailure(ip);
    // Delay artificial de 400ms para mitigar ataques de força bruta
    await new Promise((res) => setTimeout(res, 400));
    return { success: false, error: "PIN incorreto." };
  }

  recordLoginSuccess(ip);
  return { success: true };
}

export async function loginAction(password: string): Promise<{ success: boolean; error?: string }> {
  // Se a autenticação não estiver configurada no ambiente
  if (!isAuthEnabled()) {
    return {
      success: false,
      error: "APP_PASSWORD não foi configurado no arquivo .env do servidor.",
    };
  }

  // Sem AUTH_SECRET não há como assinar sessão: falha de forma clara em vez de usar a senha como chave.
  if (!getAuthSecret()) {
    return {
      success: false,
      error: "AUTH_SECRET não foi configurado no arquivo .env do servidor.",
    };
  }

  const ip = await currentClientIp();
  const gate = checkLoginAllowed(ip);
  if (!gate.allowed) {
    return { success: false, error: lockedMessage(gate.retryAfterSec) };
  }

  const isValid = validatePassword(typeof password === "string" ? password : "");
  if (!isValid) {
    recordLoginFailure(ip);
    // Delay artificial de 400ms para mitigar ataques de força bruta
    await new Promise((res) => setTimeout(res, 400));
    return { success: false, error: "Senha incorreta." };
  }
  recordLoginSuccess(ip);

  const token = await createSessionToken();
  const cookieStore = await cookies();
  const headersList = await headers();
  const proto = headersList.get("x-forwarded-proto");
  const isHttps = proto === "https";

  cookieStore.set(SESSION_COOKIE_NAME, token, {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: SESSION_MAX_AGE, // 90 dias
    secure: isHttps,
  });

  return { success: true };
}

export async function logoutAction(): Promise<void> {
  const cookieStore = await cookies();
  cookieStore.delete(SESSION_COOKIE_NAME);
  redirect("/login");
}
